import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    AlertTriangle,
    Briefcase,
    CheckCircle2,
    ChevronRight,
    FileUp,
    Loader2,
    ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import OnboardingHero from "@/components/OnboardingHero";
import TimezoneSelect from "@/components/TimezoneSelect";
import { detectTimezone } from "@/lib/timezones";
import { fetchSettings, submitRecruiting, uploadResume } from "@/lib/api";

const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const LICENSE_TYPES = [
    { value: "215", label: "215 — Life & Variable Annuity (FL)" },
    { value: "Other", label: "Other" },
];

const initial = {
    name: "",
    email: "",
    phone: "",
    state: "FL",
    license_type: "215",
    license_number: "",
    license_state: "FL",
    license_active: "",
    experience_years: 0,
    current_carrier: "",
    resume_url: "",
    resume_filename: "",
    goals: "",
    timezone: detectTimezone(),
    upline_first_name: "",
    upline_last_name: "",
    confirm_license: false,
    confirm_no_guarantee: false,
};

export default function Join() {
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        fetchSettings().then(setSettings).catch(() => {});
    }, []);

    const setField = (k, v) => {
        setForm((s) => ({ ...s, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const licenseBlocked =
        form.license_type !== "215" || form.license_active === "no";

    const onResumeChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = f.name.split(".").pop().toLowerCase();
        if (!["pdf", "docx"].includes(ext)) {
            toast.error("Only PDF or DOCX files are accepted.");
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            toast.error("File too large. Max 5 MB.");
            return;
        }
        setUploading(true);
        try {
            const { resume_url, filename } = await uploadResume(f);
            setForm((s) => ({ ...s, resume_url, resume_filename: filename }));
            toast.success("Resume uploaded.");
        } catch {
            toast.error("Couldn't upload resume.");
        } finally {
            setUploading(false);
        }
    };

    const validateLocal = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Required.";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
        const digits = (form.phone || "").replace(/\D+/g, "");
        if (digits.length < 10) e.phone = "Enter a valid 10+ digit phone.";
        if (!US_STATES.includes(form.state)) e.state = "Pick your state.";
        if (form.license_type !== "215") e.license_type = "Moving Mountains currently only accepts active Florida 215 license holders.";
        if (!/^[A-Za-z0-9]{4,20}$/.test(form.license_number)) e.license_number = "License number must be 4–20 alphanumeric characters.";
        if (!US_STATES.includes(form.license_state)) e.license_state = "Pick license state.";
        if (form.license_active !== "yes") e.license_active = "An active 215 license is required.";
        const yrs = Number(form.experience_years);
        if (Number.isNaN(yrs) || yrs < 0 || yrs > 50) e.experience_years = "0–50 only.";
        if (!form.resume_url) e.resume_url = "Please upload a resume.";
        if ((form.goals || "").trim().length < 50) e.goals = "Tell us at least 50 characters about your goals.";
        if ((form.goals || "").trim().length > 1000) e.goals = "Keep goals under 1000 characters.";
        if (!form.confirm_license) e.confirm_license = "Required.";
        if (!form.confirm_no_guarantee) e.confirm_no_guarantee = "Required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validateLocal()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            await submitRecruiting({
                name: form.name.trim(),
                email: form.email,
                phone: form.phone.trim(),
                state: form.state,
                license_type: form.license_type,
                license_number: form.license_number.trim(),
                license_state: form.license_state,
                license_active: form.license_active === "yes",
                experience_years: Number(form.experience_years),
                current_carrier: form.current_carrier.trim() || undefined,
                resume_url: form.resume_url || undefined,
                goals: form.goals.trim(),
                timezone: form.timezone || detectTimezone(),
                upline_first_name: form.upline_first_name.trim(),
                upline_last_name: form.upline_last_name.trim(),
                confirm_license: form.confirm_license,
                confirm_no_guarantee: form.confirm_no_guarantee,
            });
            setSubmitted(true);
        } catch (err) {
            const data = err?.response?.data;
            if (err?.response?.status === 422 && data?.error === "license_requirements_not_met") {
                toast.error("Active Florida 215 license is required.");
            } else if (err?.response?.status === 429) {
                toast.error("You've already applied recently — please give us a day to review.");
            } else if (data?.fields) {
                const map = {};
                data.fields.forEach((f) => { map[f.field] = f.message; });
                setErrors(map);
                toast.error("Please fix the highlighted fields.");
            } else {
                toast.error("Couldn't submit your application.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="join-page">
            <SEO
                title="Join the team — Moving Mountains Advisors"
                description="Independent, multi-carrier insurance career — remote, mentored, and built for licensed Florida 215 agents."
            />
            <section className="border-b border-mm-line bg-white">
                <div className="mm-container">
                    <OnboardingHero
                        variant="light"
                        eyebrow="Careers"
                        title="Join the Team and Change Your Life Forever."
                        subtitle="Work virtually. Be mentored. Build something of your own. We're hiring independently-minded agents who lead with honesty."
                    />
                </div>
            </section>

            <section className="mm-section bg-white">
                <div className="mm-container grid gap-10 lg:grid-cols-[1fr_1.3fr]">
                    <div className="space-y-6">
                        <div className="mm-eyebrow">Earnings disclosure</div>
                        <div className="mm-card border-amber-200 bg-amber-50/50 p-6 text-sm text-mm-ink leading-relaxed">
                            {settings?.earnings_disclosure ||
                                "With zero experience, some of our agents have earned between $10,000 to $50,000 per month conducting virtual meetings from anywhere in the world. However, results are not guaranteed and depend on effort, skill, and licensing."}
                        </div>
                        <ul className="space-y-4 text-mm-muted leading-relaxed">
                            <li>
                                <strong className="text-mm-navy">Remote-first.</strong>{" "}
                                Conduct virtual meetings from anywhere in the world.
                            </li>
                            <li>
                                <strong className="text-mm-navy">Real mentorship.</strong>{" "}
                                Licensed agents — including our founder Jeremy Felix — coach you through your first year.
                            </li>
                            <li>
                                <strong className="text-mm-navy">Independent, multi-carrier.</strong>{" "}
                                Serve clients without being locked into one company's product line.
                            </li>
                            <li>
                                <strong className="text-mm-navy">Tech that helps you sell honestly.</strong>{" "}
                                Jarvis pre-qualifies leads so you spend time where it matters.
                            </li>
                        </ul>
                        <p className="mm-disclaimer">
                            Moving Mountains currently accepts active Florida 215 license holders. Other state licensure pathways are coming soon.
                        </p>
                    </div>

                    {submitted ? (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mm-card flex flex-col items-start gap-4 p-10"
                            data-testid="join-success"
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                                <CheckCircle2 size={22} />
                            </div>
                            <h2 className="mm-display text-3xl text-mm-navy">
                                Thanks — we've received your application.
                            </h2>
                            <p className="text-mm-muted">
                                We'll review it and be in touch shortly.
                            </p>
                            <a href="/" className="mm-btn-pill mt-2 inline-flex items-center gap-1.5 bg-mm-navy text-white hover:bg-mm-blue">
                                Back home <ChevronRight size={16} />
                            </a>
                        </motion.div>
                    ) : (
                        <form onSubmit={onSubmit} className="mm-card p-7 sm:p-9" data-testid="join-form" noValidate>
                            <div className="mb-5 flex items-center gap-2 text-mm-navy">
                                <Briefcase size={18} />
                                <h3 className="mm-display text-2xl">Application</h3>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <Field label="Full name *" error={errors.name}>
                                    <input value={form.name} onChange={(e) => setField("name", e.target.value)} className="mm-input" data-testid="join-name" required />
                                </Field>
                                <Field label="Email *" error={errors.email}>
                                    <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="mm-input" data-testid="join-email" required />
                                </Field>
                                <Field label="Phone *" error={errors.phone}>
                                    <input type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="mm-input" data-testid="join-phone" required />
                                </Field>
                                <Field label="State of residence *" error={errors.state}>
                                    <select value={form.state} onChange={(e) => setField("state", e.target.value)} className="mm-input" data-testid="join-state">
                                        {US_STATES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="License type *" error={errors.license_type} className="sm:col-span-2">
                                    <select value={form.license_type} onChange={(e) => setField("license_type", e.target.value)} className="mm-input" data-testid="join-license-type">
                                        {LICENSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    {form.license_type !== "215" && (
                                        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900" data-testid="join-license-blocked">
                                            <AlertTriangle size={12} /> Moving Mountains currently only accepts active Florida 215 license holders.
                                        </p>
                                    )}
                                </Field>
                                <Field label="License number *" error={errors.license_number}>
                                    <input value={form.license_number} onChange={(e) => setField("license_number", e.target.value)} className="mm-input" placeholder="e.g. W2150021" data-testid="join-license-number" required />
                                </Field>
                                <Field label="License state *" error={errors.license_state}>
                                    <select value={form.license_state} onChange={(e) => setField("license_state", e.target.value)} className="mm-input" data-testid="join-license-state">
                                        {US_STATES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="License active? *" error={errors.license_active} className="sm:col-span-2">
                                    <div className="flex gap-3">
                                        {["yes", "no"].map((v) => (
                                            <label
                                                key={v}
                                                className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition ${form.license_active === v ? "border-mm-navy bg-mm-navy text-white" : "border-mm-line bg-white text-mm-ink hover:border-mm-blue"}`}
                                                data-testid={`join-active-${v}`}
                                            >
                                                <input type="radio" name="license_active" className="sr-only" checked={form.license_active === v} onChange={() => setField("license_active", v)} />
                                                {v === "yes" ? "Yes — active" : "No — inactive / pending"}
                                            </label>
                                        ))}
                                    </div>
                                    {form.license_active === "no" && (
                                        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-800" data-testid="join-active-blocked">
                                            <AlertTriangle size={12} /> An active 215 license is required.
                                        </p>
                                    )}
                                </Field>
                                <Field label="Years of experience *" error={errors.experience_years}>
                                    <input type="number" min={0} max={50} value={form.experience_years} onChange={(e) => setField("experience_years", e.target.value)} className="mm-input" data-testid="join-years" />
                                </Field>
                                <Field label="Current carrier / agency (optional)">
                                    <input value={form.current_carrier} onChange={(e) => setField("current_carrier", e.target.value)} className="mm-input" data-testid="join-carrier" />
                                </Field>
                                <Field label="Resume * (PDF or DOCX, max 5 MB)" error={errors.resume_url} className="sm:col-span-2">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            disabled={uploading}
                                            className="mm-btn-pill border border-mm-line bg-white text-mm-navy hover:bg-mm-bg"
                                            data-testid="join-resume-pick"
                                        >
                                            {uploading ? <Loader2 className="animate-spin" size={14} /> : <FileUp size={14} />}
                                            {form.resume_url ? "Replace file" : "Choose file"}
                                        </button>
                                        <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={onResumeChange} className="hidden" data-testid="join-resume-input" />
                                        {form.resume_filename && (
                                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                                                <CheckCircle2 size={12} /> {form.resume_filename}
                                            </span>
                                        )}
                                    </div>
                                </Field>
                                <Field label="Goals / why Moving Mountains *" error={errors.goals} className="sm:col-span-2">
                                    <textarea
                                        value={form.goals}
                                        onChange={(e) => setField("goals", e.target.value.slice(0, 1000))}
                                        rows={5}
                                        className="mm-input resize-y"
                                        placeholder="What are you looking for in your next chapter? What drew you to Moving Mountains?"
                                        data-testid="join-goals"
                                    />
                                    <div className="mt-1 text-right text-[11px] text-mm-muted">{form.goals.length}/1000 (min 50)</div>
                                </Field>
                                <Field label="Timezone" error={errors.timezone} className="sm:col-span-2">
                                    <TimezoneSelect
                                        value={form.timezone}
                                        onChange={(v) => setField("timezone", v)}
                                        testid="join-tz"
                                    />
                                </Field>
                                {/* Phase 9 — declared upline (free text; admin maps to a user at invite time). */}
                                <div className="sm:col-span-2 rounded-xl border border-mm-line bg-mm-bg/30 p-4">
                                    <div className="mm-eyebrow">Who's your upline?</div>
                                    <p className="mt-1 text-[11px] text-mm-muted">
                                        This is the person who recruited you or who will be overseeing your work at Moving Mountains.
                                    </p>
                                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <Field label="Your upline's first name *" error={errors.upline_first_name}>
                                            <input
                                                value={form.upline_first_name}
                                                onChange={(e) => setField("upline_first_name", e.target.value.slice(0, 60))}
                                                className="mm-input"
                                                placeholder="e.g. Brent"
                                                required
                                                minLength={1}
                                                maxLength={60}
                                                data-testid="join-upline-first-name"
                                            />
                                        </Field>
                                        <Field label="Your upline's last name *" error={errors.upline_last_name}>
                                            <input
                                                value={form.upline_last_name}
                                                onChange={(e) => setField("upline_last_name", e.target.value.slice(0, 60))}
                                                className="mm-input"
                                                placeholder="e.g. Deparis"
                                                required
                                                minLength={1}
                                                maxLength={60}
                                                data-testid="join-upline-last-name"
                                            />
                                        </Field>
                                    </div>
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <CheckRow
                                        checked={form.confirm_license}
                                        onChange={(v) => setField("confirm_license", v)}
                                        error={errors.confirm_license}
                                        testid="join-confirm-license"
                                    >
                                        I confirm my 215 license is active and in good standing.
                                    </CheckRow>
                                    <CheckRow
                                        checked={form.confirm_no_guarantee}
                                        onChange={(v) => setField("confirm_no_guarantee", v)}
                                        error={errors.confirm_no_guarantee}
                                        testid="join-confirm-no-guarantee"
                                    >
                                        I understand results are not guaranteed and depend on effort, skill, and licensing.
                                    </CheckRow>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-col items-start justify-between gap-3 border-t border-mm-line pt-5 sm:flex-row sm:items-center">
                                <p className="text-xs text-mm-muted max-w-md inline-flex items-start gap-1.5">
                                    <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                                    We do not sell your data. License attestations are verified before any contract is offered.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting || licenseBlocked}
                                    className="mm-btn-pill inline-flex items-center gap-2 bg-mm-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-mm-blue disabled:cursor-not-allowed disabled:opacity-50"
                                    data-testid="join-submit"
                                    title={licenseBlocked ? "Active Florida 215 license required" : "Submit application"}
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <Briefcase size={16} />}
                                    Submit application
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}

function Field({ label, error, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 block text-[12px] font-medium uppercase tracking-wider text-mm-muted">{label}</span>
            {children}
            {error ? <span className="mt-1 block text-xs text-rose-700" data-testid="field-error">{error}</span> : null}
        </label>
    );
}

function CheckRow({ checked, onChange, error, children, testid }) {
    return (
        <div>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-mm-line bg-white px-3 py-2.5 text-sm text-mm-ink hover:border-mm-blue">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-mm-navy"
                    data-testid={testid}
                />
                <span>{children}</span>
            </label>
            {error ? <span className="mt-1 block text-xs text-rose-700">{error}</span> : null}
        </div>
    );
}
